import React, { useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import type { FilterOptionsState } from "@mui/material/useAutocomplete";
import { businessTypes } from "../../become-partner-form";
import NewPhoneNumberComponent from "../../../../../common/components/new-phone-input-component/new-phone-input-component";
import { compactInputProps } from "../partner-registration-form.utils";
import { Close } from "@mui/icons-material";
import {
  addressData,
  extractAllOptions,
  filterOptionsByValue,
} from "../../../../../utils/extractCityStateZipCode";
import "./registration-forms.scss";

type Option = { label: string; value: string };

const BusinessInformationForm: React.FC<{
  formik: any;
  businessNameRef: React.RefObject<HTMLInputElement>;
  businessEmailRef: React.RefObject<HTMLInputElement>;
  businessPhoneRef: React.RefObject<HTMLInputElement>;
  businessRegistrationNumberRef: React.RefObject<HTMLInputElement>;
}> = ({
  formik,
  businessNameRef,
  businessEmailRef,
  businessRegistrationNumberRef,
}) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const allAddressOptions = extractAllOptions(addressData);
    const COUNTRY_OPTIONS: Option[] = [
      { label: "United States", value: "United States" },
    ];

    const [selectedSubcategories, setSelectedSubcategories] = React.useState<
      string[]
    >([]);
    const [subSelectValue, setSubSelectValue] = React.useState<string>("");

    useEffect(() => {
      if (Array.isArray(formik.values.serviceCoverageArea)) {
        setSelectedSubcategories(formik.values.serviceCoverageArea);
      }
    }, [formik.values.serviceCoverageArea]);

    const defaultFilter = createFilterOptions<Option>({
      matchFrom: "any",
      stringify: (o) => `${o.label} ${o.value}`,
    });
    const limitFilterOptions = (
      options: Option[],
      state: FilterOptionsState<Option>
    ) => {
      const hasQuery = (state.inputValue || "").trim().length > 0;
      const filtered = hasQuery ? defaultFilter(options, state) : options;
      return filtered.slice(0, hasQuery ? 50 : 10);
    };

    const stateOptions = React.useMemo<Option[]>(() => {
      const city = formik.values.businessCity;
      const zipCode = formik.values.businessZipCode;
      if (city && zipCode) {
        return filterOptionsByValue(
          addressData,
          "City",
          city,
          "Code",
          zipCode
        ).stateOptions.sort((a, b) => a.label.localeCompare(b.label));
      }
      if (city) {
        return filterOptionsByValue(addressData, "City", city).stateOptions.sort(
          (a, b) => a.label.localeCompare(b.label)
        );
      }
      if (zipCode) {
        return filterOptionsByValue(
          addressData,
          "Code",
          zipCode
        ).stateOptions.sort((a, b) => a.label.localeCompare(b.label));
      }
      return allAddressOptions.stateOptions.sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    }, [formik.values.businessCity, formik.values.businessZipCode]);

    const cityOptions = React.useMemo<Option[]>(() => {
      const state = formik.values.businessState;
      const zipCode = formik.values.businessZipCode;
      if (state && zipCode) {
        return filterOptionsByValue(
          addressData,
          "State",
          state,
          "Code",
          zipCode
        ).cityOptions.sort((a, b) => a.label.localeCompare(b.label));
      }
      if (state) {
        return filterOptionsByValue(addressData, "State", state).cityOptions.sort(
          (a, b) => a.label.localeCompare(b.label)
        );
      }
      if (zipCode) {
        return filterOptionsByValue(
          addressData,
          "Code",
          zipCode
        ).cityOptions.sort((a, b) => a.label.localeCompare(b.label));
      }
      return allAddressOptions.cityOptions.sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    }, [formik.values.businessState, formik.values.businessZipCode]);

    const handleCityChange = async (_: any, newValue: Option | string | null) => {
      const val = typeof newValue === "string" ? newValue : newValue?.value || "";
      if (val) {
        const { stateOptions, postCodeOptions } = filterOptionsByValue(
          addressData,
          "City",
          val
        );
        const autoState = stateOptions.length === 1 ? stateOptions[0].value : formik.values.businessState;
        const autoZip = postCodeOptions.length === 1 ? postCodeOptions[0].value : formik.values.businessZipCode;
        // Set all values atomically
        await formik.setValues({
          ...formik.values,
          businessCity: val,
          businessState: autoState,
          businessZipCode: autoZip,
          serviceCoverageArea: [],
        }, false);
        // Set touched atomically: city is touched (user acted), auto-filled fields are un-touched to clear their errors
        formik.setTouched({
          ...formik.touched,
          businessCity: true,
          businessState: stateOptions.length === 1 ? false : formik.touched.businessState,
          businessZipCode: postCodeOptions.length === 1 ? false : formik.touched.businessZipCode,
        }, true);
      } else {
        await formik.setValues({
          ...formik.values,
          businessCity: "",
          businessZipCode: "",
          serviceCoverageArea: [],
        }, false);
        formik.setTouched({ ...formik.touched, businessCity: true }, true);
      }
    };

    const coverageCityOptions = React.useMemo<Option[]>(() => {
      const zip = formik.values.businessZipCode;
      if (zip) {
        return filterOptionsByValue(addressData, "Code", zip).cityOptions.sort(
          (a, b) => a.label.localeCompare(b.label)
        );
      }
      return [];
    }, [formik.values.businessZipCode]);

    const handleAddSubcategory = (value: string) => {
      if (!value) return;
      setSelectedSubcategories((prev) => {
        const next = prev.includes(value) ? prev : [...prev, value];
        formik.setFieldValue("serviceCoverageArea", next);
        formik.setFieldTouched("serviceCoverageArea", true, false);
        return next;
      });
      setSubSelectValue("");
    };

    const handleRemoveSubcategory = (value: string) => {
      setSelectedSubcategories((prev) => {
        const next = prev.filter((v) => v !== value);
        formik.setFieldValue("serviceCoverageArea", next);
        formik.setFieldTouched("serviceCoverageArea", true, false);
        return next;
      });
    };

    function handleChange(event: {
      target: { name: string; value: string };
    }): void {
      const { name, value } = event.target;
      formik.setFieldValue(name, value);
    }

    const handleLicenseFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;
      formik.setFieldValue("licenseDocument", file);
      formik.setFieldTouched("licenseDocument", true, false);
    };

    const handleNoSpaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const newValue = value.replace(/\s/g, "");
      formik.setFieldValue(name, newValue);
    };

    const onCountryChange = (e: SelectChangeEvent) => {
      const value = e.target.value;
      formik.setFieldValue("businessCountry", value);
      formik.setFieldTouched("businessCountry", true, false);
    };

    const handleStateChange = async (_: any, newValue: Option | string | null) => {
      const val = typeof newValue === "string" ? newValue : newValue?.value || "";
      if (val) {
        const { postCodeOptions } = filterOptionsByValue(
          addressData,
          "State",
          val
        );
        const autoZip = postCodeOptions.length === 1 ? postCodeOptions[0].value : formik.values.businessZipCode;
        // Set all values atomically
        await formik.setValues({
          ...formik.values,
          businessState: val,
          businessZipCode: autoZip,
          serviceCoverageArea: [],
        }, false);
        // Set touched atomically: state is touched, auto-filled zip is un-touched to clear its error
        formik.setTouched({
          ...formik.touched,
          businessState: true,
          businessZipCode: postCodeOptions.length === 1 ? false : formik.touched.businessZipCode,
        }, true);
      } else {
        await formik.setValues({
          ...formik.values,
          businessState: "",
          businessCity: "",
          businessZipCode: "",
          serviceCoverageArea: [],
        }, false);
        formik.setTouched({ ...formik.touched, businessState: true }, true);
      }
    };

    const handleZipCodeChange = async (_: any, newValue: Option | string | null) => {
      const val = typeof newValue === "string" ? newValue : newValue?.value || "";
      if (val) {
        const { cityOptions, stateOptions } = filterOptionsByValue(
          addressData,
          "Code",
          val
        );
        const autoCity = cityOptions.length === 1 ? cityOptions[0].value : formik.values.businessCity;
        const autoState = stateOptions.length === 1 ? stateOptions[0].value : formik.values.businessState;
        // Set all values atomically
        await formik.setValues({
          ...formik.values,
          businessZipCode: val,
          businessCity: autoCity,
          businessState: autoState,
          serviceCoverageArea: [],
        }, false);
        // Set touched atomically: zip is touched, auto-filled city/state are un-touched to clear their errors
        formik.setTouched({
          ...formik.touched,
          businessZipCode: true,
          businessCity: cityOptions.length === 1 ? false : formik.touched.businessCity,
          businessState: stateOptions.length === 1 ? false : formik.touched.businessState,
        }, true);
      } else {
        await formik.setValues({
          ...formik.values,
          businessZipCode: "",
          serviceCoverageArea: [],
        }, false);
        formik.setTouched({ ...formik.touched, businessZipCode: true }, true);
      }
    };

    const cityByValue = React.useMemo(
      () => new Map(cityOptions.map((o) => [o.value, o])),
      [cityOptions]
    );

    const stateByValue = React.useMemo(
      () => new Map(stateOptions.map((o) => [o.value, o])),
      [stateOptions]
    );

    const zipOptions = React.useMemo<Option[]>(() => {
      const state = formik.values.businessState;
      const city = formik.values.businessCity;
      if (state && city) {
        return filterOptionsByValue(
          addressData,
          "City",
          city,
          "State",
          state
        ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
      }
      if (state) {
        return filterOptionsByValue(
          addressData,
          "State",
          state
        ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
      }
      if (city) {
        return filterOptionsByValue(
          addressData,
          "City",
          city
        ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
      }
      return allAddressOptions.postCodeOptions.sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    }, [formik.values.businessState, formik.values.businessCity]);

    const zipByValue = React.useMemo(
      () => new Map(zipOptions.map((o) => [o.value, o])),
      [zipOptions]
    );

    const normalizeWebsite = (raw: string) => {
      const value = (raw || "").trim();
      if (!value) return value;
      if (/^https?:\/\//i.test(value)) return value;
      if (/^www\./i.test(value)) return `https://${value}`;
      if (/^[a-z0-9.-]+\.[a-z]{2,}(:\d+)?(\/.*)?$/i.test(value)) {
        return `https://${value}`;
      }
      return value;
    };

    return (
      <Box
        className="business-information-form"
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.23)",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.23)",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
          {
            borderWidth: "1px",
            borderColor: "rgba(0, 0, 0, 0.23)",
          },
          "& .MuiFormHelperText-root": {
            margin: 0,
            padding: 0,
            lineHeight: "16px",
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              Business Name<span>*</span>
            </Typography>
            <TextField
              inputRef={businessNameRef}
              fullWidth
              name="businessName"
              required
              placeholder="Enter business name"
              InputProps={compactInputProps}
              inputProps={{ maxLength: 50 }}
              value={formik.values.businessName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.businessName && Boolean(formik.errors.businessName)
              }
              helperText={
                formik.touched.businessName && formik.errors.businessName
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <Typography className="form-label">
                Business Type<span>*</span>
              </Typography>
              <Select
                id="businessType"
                name="businessType"
                displayEmpty
                required
                value={formik.values.businessType}
                onChange={formik.handleChange}
                slotProps={{
                  root: compactInputProps,
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.businessType &&
                  Boolean(formik.errors.businessType)
                }
                sx={{
                  input: {
                    color: "#104145 !important",
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span style={{ color: "rgba(0, 0, 0, 0.4)" }}>
                        Select a business type
                      </span>
                    );
                  }
                  const selectedType = businessTypes.find(
                    (type) => type.value === selected
                  );
                  return selectedType ? selectedType.type : selected;
                }}
              >
                <MenuItem value="" disabled>
                  Select a business type
                </MenuItem>
                {businessTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.type}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.businessType && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{
                    fontSize: "12px",
                  }}
                >
                  {formik.errors.businessType}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">Years in Operation</Typography>
            <TextField
              fullWidth
              name="yearsinOperation"
              placeholder="Enter years in operation"
              InputProps={compactInputProps}
              value={formik.values.yearsinOperation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.yearsinOperation &&
                Boolean(formik.errors.yearsinOperation)
              }
              helperText={
                formik.touched.yearsinOperation && formik.errors.yearsinOperation
              }
              type="number"
              inputProps={{ min: 0, max: 9999, maxLength: 4 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              Business Address<span>*</span>
            </Typography>
            <TextField
              fullWidth
              name="businessAddress"
              required
              placeholder="Enter business address"
              InputProps={compactInputProps}
              value={formik.values.businessAddress}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.businessAddress &&
                Boolean(formik.errors.businessAddress)
              }
              inputProps={{ maxLength: 255 }}
              helperText={
                formik.touched.businessAddress && formik.errors.businessAddress
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              City<span>*</span>
            </Typography>
            <Autocomplete
              fullWidth
              freeSolo
              options={cityOptions}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
              value={cityByValue.get(formik.values.businessCity) || formik.values.businessCity || null}
              onChange={handleCityChange}
              onInputChange={(_, newInputValue) => {
                formik.setFieldValue("businessCity", newInputValue, true);
              }}
              onBlur={formik.handleBlur}
              filterOptions={limitFilterOptions}
              isOptionEqualToValue={(o, v) => (typeof v === "string" ? o.value === v : o.value === v.value)}
              openOnFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="businessCity"
                  required
                  placeholder="Select a city"
                  InputProps={{
                    ...params.InputProps,
                    ...compactInputProps,
                  }}
                  error={
                    formik.touched.businessCity &&
                    Boolean(formik.errors.businessCity)
                  }
                  helperText={
                    formik.touched.businessCity && formik.errors.businessCity
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              State<span>*</span>
            </Typography>
            <Autocomplete
              fullWidth
              freeSolo
              options={stateOptions}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
              value={stateByValue.get(formik.values.businessState) || formik.values.businessState || null}
              onChange={handleStateChange}
              onInputChange={(_, newInputValue) => {
                formik.setFieldValue("businessState", newInputValue, true);
              }}
              onBlur={formik.handleBlur}
              filterOptions={limitFilterOptions}
              isOptionEqualToValue={(o, v) => (typeof v === "string" ? o.value === v : o.value === v.value)}
              openOnFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="businessState"
                  required
                  placeholder="Select a state"
                  InputProps={{
                    ...params.InputProps,
                    ...compactInputProps,
                  }}
                  error={
                    formik.touched.businessState &&
                    Boolean(formik.errors.businessState)
                  }
                  helperText={
                    formik.touched.businessState && formik.errors.businessState
                  }
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              Zip Code<span>*</span>
            </Typography>
            <Autocomplete
              fullWidth
              freeSolo
              options={zipOptions}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
              value={zipByValue.get(formik.values.businessZipCode) || formik.values.businessZipCode || null}
              onChange={handleZipCodeChange}
              onInputChange={(_, newInputValue) => {
                formik.setFieldValue("businessZipCode", newInputValue, true);
              }}
              onBlur={formik.handleBlur}
              filterOptions={limitFilterOptions}
              isOptionEqualToValue={(o, v) => (typeof v === "string" ? o.value === v : o.value === v.value)}
              openOnFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="businessZipCode"
                  required
                  placeholder="Select a zip code"
                  InputProps={{
                    ...params.InputProps,
                    ...compactInputProps,
                  }}
                  error={
                    formik.touched.businessZipCode &&
                    Boolean(formik.errors.businessZipCode)
                  }
                  helperText={
                    formik.touched.businessZipCode && formik.errors.businessZipCode
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              Country<span>*</span>
            </Typography>
            <Select
              fullWidth
              name="businessCountry"
              required
              slotProps={{
                root: compactInputProps,
              }}
              displayEmpty
              sx={{
                input: {
                  color: "#104145 !important",
                },
              }}
              value={formik.values.businessCountry || ""}
              onChange={onCountryChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.businessCountry &&
                Boolean(formik.errors.businessCountry)
              }
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <span style={{ color: "rgba(0, 0, 0, 0.4)" }}>
                      Select country
                    </span>
                  );
                }
                return selected;
              }}
            >
              <MenuItem value="" disabled>
                Select country
              </MenuItem>
              {COUNTRY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.businessCountry && (
              <Typography
                variant="body2"
                color="error"
                sx={{ fontSize: "12px", marginTop: "4px" }}
              >
                {formik.errors.businessCountry}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              Business Phone
              <span>*</span>
            </Typography>
            <Box
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px !important",
                  height: "39px !important",
                  maxWidth: "100%",
                },
                "& .MuiInputBase-root": {
                  fontSize: "14px",
                  height: "39px !important",
                  padding: "10px 6px !important",
                },
                "& .MuiInputAdornment-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "6px 0 0 6px",
                },
                "& input": {
                  color: "#104145 !important",
                },
              }}
            >
              <NewPhoneNumberComponent
                value={formik.values.businessPhone}
                onChange={(mobileValue: string) => {
                  const cleanedValue = String(mobileValue || "").replace(/\D/g, "");
                  formik.setFieldValue("businessPhone", cleanedValue);
                  formik.setFieldTouched("businessPhone", true, false);
                }}
                countryCode={formik.values.businessPhoneCC}
                onCountryChange={(value: string) =>
                  formik.setFieldValue("businessPhoneCC", value)
                }
                placeholder="Enter business phone number"
                error={
                  formik.touched.businessPhone &&
                  Boolean(formik.errors.businessPhone)
                }
                helperText={
                  formik.touched.businessPhone && formik.errors.businessPhone
                }
                showHelper={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              Business Email<span>*</span>
            </Typography>
            <TextField
              inputRef={businessEmailRef}
              fullWidth
              name="businessEmail"
              required
              type="email"
              placeholder="Enter business email"
              InputProps={compactInputProps}
              value={formik.values.businessEmail}
              onChange={handleNoSpaceChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.businessEmail &&
                Boolean(formik.errors.businessEmail)
              }
              inputProps={{ maxLength: 150 }}
              helperText={
                formik.touched.businessEmail && formik.errors.businessEmail
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              Business Website
            </Typography>
            <TextField
              fullWidth
              name="businessWebsite"
              placeholder="Enter business website"
              InputProps={compactInputProps}
              value={formik.values.businessWebsite}
              onChange={handleNoSpaceChange}
              onBlur={async (e) => {
                const normalized = normalizeWebsite(e.target.value);
                if (normalized !== formik.values.businessWebsite) {
                  await formik.setFieldValue("businessWebsite", normalized, false);
                }
                await formik.setFieldTouched("businessWebsite", true, true);
                formik.handleBlur(e);
              }}
              inputProps={{ maxLength: 255 }}
              error={
                formik.touched.businessWebsite &&
                Boolean(formik.errors.businessWebsite)
              }
              helperText={
                formik.touched.businessWebsite && formik.errors.businessWebsite
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              Business Registration Number / License ID
            </Typography>
            <TextField
              inputRef={businessRegistrationNumberRef}
              fullWidth
              name="businessRegistrationNumber"
              placeholder="Enter business registration number"
              InputProps={compactInputProps}
              value={formik.values.businessRegistrationNumber}
              onChange={handleNoSpaceChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.businessRegistrationNumber &&
                Boolean(formik.errors.businessRegistrationNumber)
              }
              inputProps={{ maxLength: 30 }}
              helperText={
                formik.touched.businessRegistrationNumber &&
                formik.errors.businessRegistrationNumber
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {/* <Grid item xs={12} sm={6}>
            <Typography className="form-label">Operation Hours</Typography>
            <TextField
              fullWidth
              name="operationHours"
              placeholder="Enter operation hours"
              InputProps={compactInputProps}
              value={formik.values.operationHours}
              onChange={handleNoSpaceChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.operationHours &&
                Boolean(formik.errors.operationHours)
              }
              inputProps={{ maxLength: 255 }}
              helperText={
                formik.touched.operationHours && formik.errors.operationHours
              }
            />
          </Grid> */}
           <Grid item xs={12} sm={6}>
            <Typography className="form-label-service-coverage">
              Service Coverage Area{" "}
              <span className="service-coverage-note">
                (You can add more serviceable areas from your dashboard)
              </span>
            </Typography>
            {/* <Typography className="form-label-sub-text">
              (You can add more serviceable areas from your dashboard)
            </Typography> */}
            <Select
              fullWidth
              value={subSelectValue}
              onChange={(e) =>
                handleAddSubcategory((e.target as HTMLInputElement).value)
              }
              slotProps={{
                root: compactInputProps,
              }}
              displayEmpty
              sx={{
                input: {
                  color: "#104145 !important",
                },
              }}
              error={
                formik.touched.serviceCoverageArea &&
                Boolean(formik.errors.serviceCoverageArea)
              }
              disabled={!formik.values.businessZipCode}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <span style={{ color: "rgba(0, 0, 0, 0.4)" }}>
                      {formik.values.businessZipCode
                        ? "Select coverage city"
                        : "Select zip code first"}
                    </span>
                  );
                }
                return selected;
              }}
            >
              <MenuItem value="" disabled>
                {formik.values.businessZipCode
                  ? "Select coverage city"
                  : "Select zip code first"}
              </MenuItem>
              {coverageCityOptions.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
            >
              {selectedSubcategories.map((sid) => (
                <Chip
                  key={sid}
                  label={sid}
                  onDelete={() => handleRemoveSubcategory(sid)}
                  size="small"
                  sx={{
                    background: "rgba(16, 65, 69, 0.3)",
                    color: "#104145",
                    borderRadius: "8px",
                  }}
                />
              ))}
            </Stack>
            {formik.touched.serviceCoverageArea &&
              formik.errors.serviceCoverageArea && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ fontSize: "12px", mt: 0.5 }}
                >
                  {formik.errors.serviceCoverageArea}
                </Typography>
              )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              Upload business license document
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={handleLicenseFileSelect}
              onBlur={() => formik.setFieldTouched("licenseDocument", true)}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            />

            <TextField
              fullWidth
              name="licenseDocument"
              placeholder="Upload business license document"
              value={formik.values.licenseDocument?.name || ""}
              onBlur={() => formik.setFieldTouched("licenseDocument", true)}
              InputProps={{
                ...compactInputProps,
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    {formik.values.licenseDocument && (
                      <IconButton
                        aria-label="remove license document"
                        size="small"
                        onClick={() => {
                          formik.setFieldValue("licenseDocument", null);
                        }}
                        sx={{
                          height: 22,
                          width: 22,
                          color: "#104145",
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        textTransform: "none",
                        borderRadius: "0 4px 4px 0",
                        boxShadow: "none",
                        width: "74px",
                        height: "39px",
                        background: "#DCDCDC",
                        color: "#104145",
                        fontWeight: 500,
                        fontSize: "12px",
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: "none",
                        },
                        right: "-13px",
                        top: "-1px",
                      }}
                    >
                      Upload
                    </Button>
                  </InputAdornment>
                ),
              }}
              error={
                formik.touched.licenseDocument &&
                Boolean(formik.errors.licenseDocument)
              }
              helperText={
                formik.touched.licenseDocument && formik.errors.licenseDocument
              }
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

export default BusinessInformationForm;
